import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Star, Gift, TrendingUp, Clock, ArrowLeft, Trophy, Zap, Users, Copy, Check, Share2, Rocket, Target } from 'lucide-react';
import { BRAND } from '../brand';

const LEVEL_STYLES = {
  BRONZE: { bg: 'from-amber-700 to-amber-900', accent: '#CD7F32' },
  SILVER: { bg: 'from-gray-400 to-gray-600', accent: '#C0C0C0' },
  GOLD: { bg: 'from-yellow-500 to-yellow-700', accent: '#FFD700' },
};

const MISSION_ICONS = {
  rocket: Rocket, zap: Zap, users: Users, trophy: Trophy, star: Star,
};

const GBPuntosClub = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);
  const [tab, setTab] = useState('rewards');
  const [copied, setCopied] = useState(false);
  const [promo, setPromo] = useState(null);
  const [referralPromo, setReferralPromo] = useState(null);
  const [minBonusClp, setMinBonusClp] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/gb/status');
      setData(res.data);
      // Cargar elegibilidad para invitar
      try {
        const refInfo = await api.get('/gb/referral-info');
        setData(prev => ({ ...(prev || res.data), can_refer: refInfo.data.can_refer, ineligible_reason: refInfo.data.ineligible_reason }));
      } catch (err) {
        console.error('referral-info error:', err);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    api.get('/gb/promo/status').then(res => {
      if (res.data.active) setPromo(res.data);
    }).catch(() => {});
    api.get('/gb/referral-promo').then(res => {
      if (res.data.active) setReferralPromo(res.data);
    }).catch(() => {});
    // Mínimo de envío para aplicar el ticket — fuente única: backend.
    api.get('/gb/pending-bonus').then(res => {
      if (res.data?.min_transfer_clp != null) setMinBonusClp(res.data.min_transfer_clp);
    }).catch(() => {});
  }, []);

  const handleRedeem = async (reward) => {
    if (!window.confirm(`Canjear ${reward.points} puntos por ${reward.label} de bonus?\n\nSe aplicara automaticamente en tu proximo envio.`)) return;
    setRedeeming(reward.points);
    try {
      await api.post('/gb/redeem', { reward_points: reward.points });
      alert(`Has canjeado ${reward.label} de bonus. Se aplicara automaticamente en tu proximo envio.`);
      fetchStatus();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al canjear');
    } finally {
      setRedeeming(null);
    }
  };

  const copyReferralLink = () => {
    if (!data?.referral_code) return;
    const link = `${window.location.origin}/r/${data.referral_code}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareReferral = () => {
    if (!data?.referral_code) return;
    const link = `${window.location.origin}/r/${data.referral_code}`;
    const promoText = referralPromo
      ? `🔥 PROMO: ambos ganamos ${referralPromo.bonus_points} pts ($3.000 CLP) si haces tu primer envío de $100.000+ este finde.`
      : `Ambos ganamos 300 puntos cuando hagas tu primer envío de $100.000+.`;
    const destName = (BRAND?.exchange?.primary?.name) || '';
    const text = `Te invito a ${BRAND.name}${destName ? ` — envía dinero a ${destName} fácil, rápido y seguro` : ''}. ${promoText} ${link}`;
    if (navigator.share) {
      navigator.share({ title: `${BRAND.name} - Invitacion`, text });
    } else {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin h-10 w-10 border-4 border-yellow-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const levelStyle = LEVEL_STYLES[data.level] || LEVEL_STYLES.BRONZE;
  const minBonusLabel = minBonusClp != null ? `$${Number(minBonusClp).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP` : '';

  // Estado de bienvenida: cliente sin puntos. Usa los premios que entrega el backend
  // (no hardcodeamos montos) para mostrar el primer premio y cuánto falta.
  const isNewUser = (data.points || 0) === 0;
  const rewardsSorted = [...(data.available_rewards || [])].sort((a, b) => a.points - b.points);
  const firstReward = rewardsSorted[0];
  const pointsToFirst = firstReward ? Math.max(firstReward.points - (data.points || 0), 0) : 0;

  return (
    <div className="max-w-lg mx-auto px-4 py-6 pb-28">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate('/dashboard')} className="w-10 h-10 rounded-xl bg-white border flex items-center justify-center hover:bg-gray-50" data-testid="back-button">
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div>
          <h1 className="text-lg font-bold text-gray-900" data-testid="gb-title">{BRAND.puntosProgramName} Club</h1>
          <p className="text-xs text-gray-500">Programa de fidelizacion {BRAND.name}</p>
        </div>
      </div>

      {/* Level Card */}
      <div className={`bg-gradient-to-br ${levelStyle.bg} rounded-2xl p-5 mb-4 text-white relative overflow-hidden`} data-testid="level-card">
        <div className="absolute top-0 right-0 opacity-10">
          <Trophy size={120} />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-1">
            <Star size={16} fill="currentColor" />
            <span className="text-xs font-bold uppercase tracking-wider opacity-80">Nivel {data.level}</span>
          </div>
          <p className="text-4xl font-black mt-1" data-testid="points-display">
            {(data.points || 0).toLocaleString('es-CL', { timeZone: 'America/Santiago' })}
          </p>
          <p className="text-sm opacity-80">puntos disponibles</p>
          <div className="flex items-center gap-4 mt-3 text-xs opacity-70">
            <span>Multiplicador: <strong className="opacity-100 text-sm">{data.multiplier}x</strong></span>
            <span>6 meses: <strong className="opacity-100 text-sm">{(data.points_6_months || 0).toLocaleString('es-CL', { timeZone: 'America/Santiago' })} pts</strong></span>
          </div>
        </div>
      </div>

      {/* Estado de bienvenida (cliente sin puntos) */}
      {isNewUser && firstReward && (
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-2xl p-5 mb-4" data-testid="gb-welcome-empty">
          <div className="flex items-center gap-2 mb-2">
            <Star size={18} className="text-yellow-600" fill="currentColor" />
            <p className="font-bold text-gray-900 text-sm">¡Bienvenido a {BRAND.puntosProgramName}!</p>
          </div>
          <p className="text-sm text-gray-700">
            Ganas puntos en cada envío que haces y al invitar amigos. Luego los canjeas por un <strong>bonus en CLP</strong> que se suma a lo que recibe tu destinatario.
          </p>
          <div className="mt-3 bg-white rounded-xl border border-yellow-200 p-3" data-testid="gb-welcome-first-reward">
            <p className="text-xs text-gray-500 mb-1">Tu primer premio</p>
            <div className="flex items-center justify-between">
              <p className="font-bold text-gray-800 text-sm">{firstReward.label}</p>
              <p className="text-xs font-bold text-yellow-700">{firstReward.points.toLocaleString('es-CL', { timeZone: 'America/Santiago' })} pts</p>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mt-2">
              <div className="h-full rounded-full bg-yellow-400" style={{ width: '0%' }} />
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              Te faltan <strong className="text-gray-700">{pointsToFirst.toLocaleString('es-CL', { timeZone: 'America/Santiago' })}</strong> puntos para tu primer premio.
            </p>
          </div>
          <button
            onClick={() => navigate('/send-money')}
            className="mt-3 w-full py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition"
            data-testid="gb-welcome-cta"
          >
            Hacer mi primer envío
          </button>
        </div>
      )}

      {/* Pending Bonus Banner */}
      {data.pending_bonus_clp > 0 && (
        <div className="bg-green-50 border-2 border-green-300 rounded-2xl p-4 mb-4 flex items-center gap-3" data-testid="pending-bonus-banner">
          <div className="w-10 h-10 rounded-full bg-green-200 flex items-center justify-center flex-shrink-0">
            <Gift size={20} className="text-green-700" />
          </div>
          <div>
            <p className="font-bold text-green-800 text-sm">Bonus pendiente: ${data.pending_bonus_clp.toLocaleString('es-CL', { timeZone: 'America/Santiago' })} CLP</p>
            <p className="text-xs text-green-600">Se aplica en tu proximo envio de {minBonusLabel} o mas</p>
          </div>
        </div>
      )}

      {/* Promo Banner */}
      {promo && (
        <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-2xl p-4 mb-4" data-testid="promo-banner-gb">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-200 flex items-center justify-center flex-shrink-0">
              <Star size={20} className="text-yellow-600" fill="currentColor" />
            </div>
            <div>
              <p className="font-bold text-amber-800 text-sm">Promo activa: +{promo.bonus_points} puntos de regalo</p>
              <p className="text-xs text-amber-600">En tu primer envio desde Chile de ${(promo.min_amount_clp || 100000).toLocaleString('es-CL', { timeZone: 'America/Santiago' })}+ CLP. Puntos extra adicionales a los normales.</p>
            </div>
          </div>
        </div>
      )}

      {/* Progress to Next Level */}
      {data.next_level && (
        <div className="bg-white rounded-2xl border p-4 mb-4" data-testid="level-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-gray-700">Progreso a {data.next_level.name}</span>
            <span className="text-xs font-bold text-gray-500">{data.next_level.progress_percent}%</span>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-1000"
              style={{
                width: `${data.next_level.progress_percent}%`,
                background: `linear-gradient(90deg, ${levelStyle.accent}, ${LEVEL_STYLES[data.next_level.name]?.accent || '#FFD700'})`,
              }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1.5">
            Faltan <strong className="text-gray-700">{(data.next_level.points_remaining || 0).toLocaleString('es-CL', { timeZone: 'America/Santiago' })}</strong> puntos en 6 meses
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1.5 mb-4 overflow-x-auto">
        {[
          { id: 'rewards', label: 'Canjear', icon: <Gift size={13} /> },
          { id: 'missions', label: 'Misiones', icon: <Target size={13} /> },
          { id: 'referral', label: 'Invitar', icon: <Users size={13} /> },
          { id: 'history', label: 'Historial', icon: <Clock size={13} /> },
          { id: 'how', label: 'Info', icon: <TrendingUp size={13} /> },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center justify-center gap-1 py-2 px-3 rounded-xl text-xs font-semibold transition whitespace-nowrap ${
              tab === t.id ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 border hover:bg-gray-50'
            }`}
            data-testid={`tab-${t.id}`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* REWARDS TAB */}
      {tab === 'rewards' && (
        <div className="space-y-3" data-testid="rewards-list">
          {/* Cuadro educativo: cómo funciona el canje */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl p-4" data-testid="gb-howto-card">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                <Gift size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-green-900 text-sm">¿Cómo usar tus puntos?</p>
                <ol className="text-xs text-green-800 mt-1.5 space-y-1 list-decimal pl-4">
                  <li>Acumula puntos enviando dinero o invitando amigos.</li>
                  <li>Aquí abajo elige el premio que quieres y aprieta <strong>Canjear</strong>.</li>
                  <li>Recibes un <strong>bonus en CLP</strong> que se suma a tu próximo envío de $50.000 CLP o más.</li>
                </ol>
              </div>
            </div>
          </div>
          {(data.available_rewards || []).map((r, i) => (
            <div
              key={i}
              className={`bg-white rounded-2xl border-2 p-4 transition ${
                r.can_redeem ? 'border-green-200 hover:border-green-400' : 'border-gray-100 opacity-60'
              }`}
              data-testid={`reward-${r.points}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-black ${
                    r.can_redeem ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {r.bonus_clp >= 7000 ? <Trophy size={24} /> : r.bonus_clp >= 3000 ? <Zap size={24} /> : <Gift size={24} />}
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">{r.label}</p>
                    <p className="text-xs text-gray-500">{r.points.toLocaleString('es-CL', { timeZone: 'America/Santiago' })} puntos</p>
                  </div>
                </div>
                <button
                  onClick={() => handleRedeem(r)}
                  disabled={!r.can_redeem || redeeming === r.points}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition active:scale-95 ${
                    r.can_redeem
                      ? 'bg-green-700 text-white hover:bg-green-800'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  data-testid={`redeem-${r.points}`}
                >
                  {redeeming === r.points ? '...' : 'Canjear'}
                </button>
              </div>
            </div>
          ))}
          <div className="bg-blue-50 rounded-xl p-3 mt-2">
            <p className="text-xs text-blue-700">El bonus se suma al monto que recibe tu destinatario. Solo aplica en envios de <strong>{minBonusLabel}</strong> o mas. Solo puedes tener 1 bonus activo a la vez.</p>
          </div>
        </div>
      )}

      {/* MISSIONS TAB */}
      {tab === 'missions' && (
        <div className="space-y-3" data-testid="missions-list">
          {(data.missions || []).map((m) => {
            const IconComp = MISSION_ICONS[m.icon] || Star;
            const pct = m.target > 0 ? Math.min(100, Math.round((m.progress / m.target) * 100)) : 0;
            return (
              <div
                key={m.id}
                className={`bg-white rounded-2xl border-2 p-4 transition ${
                  m.completed ? 'border-green-200 bg-green-50/30' : 'border-gray-100'
                }`}
                data-testid={`mission-${m.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                    m.completed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    <IconComp size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-gray-800 text-sm truncate">{m.title}</p>
                      {m.completed && <Check size={14} className="text-green-600 flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-gray-500">{m.description}</p>
                    {!m.completed && (
                      <div className="mt-1.5 flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[10px] text-gray-400 font-medium">{m.progress}/{m.target}</span>
                      </div>
                    )}
                  </div>
                  <div className={`text-right flex-shrink-0 ${m.completed ? 'text-green-600' : 'text-gray-400'}`}>
                    <p className="text-sm font-black">+{m.points}</p>
                    <p className="text-[10px]">pts</p>
                  </div>
                </div>
              </div>
            );
          })}
          {(!data.missions || data.missions.length === 0) && (
            <div className="bg-white rounded-2xl border p-8 text-center">
              <Target size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500 text-sm">No hay misiones disponibles</p>
            </div>
          )}
          <div className="bg-amber-50 rounded-xl p-3">
            <p className="text-xs text-amber-700">Las misiones mensuales se reinician el primer dia de cada mes.</p>
          </div>
        </div>
      )}

      {/* REFERRAL TAB */}
      {tab === 'referral' && (
        <div className="space-y-4" data-testid="referral-section">
          {/* WARNING: usuario no elegible para invitar */}
          {data.can_refer === false && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4" data-testid="cannot-refer-banner">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-700 font-bold text-lg">!</span>
                </div>
                <div className="flex-1">
                  <p className="font-bold text-amber-900 text-sm">Aún no puedes invitar amigos</p>
                  <p className="text-xs text-amber-700 mt-1">{data.ineligible_reason || 'Completa tu KYC para activar tu código.'}</p>
                  {data.ineligible_reason && data.ineligible_reason.includes('KYC 2026') && (
                    <button
                      onClick={() => navigate('/perfil/kyc-2026')}
                      className="mt-2 text-xs font-bold bg-amber-700 text-white px-3 py-1.5 rounded-lg hover:bg-amber-800"
                    >Actualizar KYC ahora →</button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* PROMO BOOST BANNER */}
          {referralPromo && (
            <div className="bg-gradient-to-r from-pink-500 via-red-500 to-orange-500 rounded-2xl p-4 text-white shadow-lg" data-testid="referral-promo-banner">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
                  <Zap size={26} fill="currentColor" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wider font-bold opacity-90">🔥 PROMO ACTIVA</p>
                  <p className="font-black text-lg leading-tight">+{referralPromo.bonus_points} pts por referido</p>
                  <p className="text-xs opacity-90 mt-0.5">Canje directo a <strong>$3.000 CLP</strong> de bonus en tu próximo envío</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border p-5 text-center">
            <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-3">
              <Users size={28} className="text-green-700" />
            </div>
            <h3 className="font-bold text-gray-800 text-base mb-1">Invita a tus amigos</h3>
            <p className="text-sm text-gray-500 mb-4">
              Ambos ganan <strong className="text-green-700">{referralPromo ? referralPromo.bonus_points : 300} puntos</strong> cuando tu amigo hace su primer envío de <strong>$100.000 CLP+</strong> por App o WhatsApp
            </p>

            {data.referral_code && (
              <>
                <div className="bg-gray-50 rounded-xl p-4 mb-3">
                  <p className="text-xs text-gray-500 mb-1">Tu código de referido</p>
                  <p className="text-2xl font-black text-gray-900 tracking-widest" data-testid="referral-code">{data.referral_code}</p>
                  <p className="text-[10px] text-gray-400 mt-1">{BRAND.domain}/r/{data.referral_code}</p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={copyReferralLink}
                    className="flex-1 flex items-center justify-center gap-2 bg-gray-900 text-white py-3 rounded-xl font-semibold text-sm transition active:scale-95"
                    data-testid="copy-referral-btn"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? 'Copiado!' : 'Copiar enlace'}
                  </button>
                  <button
                    onClick={shareReferral}
                    className="flex items-center justify-center gap-2 bg-green-700 text-white px-5 py-3 rounded-xl font-semibold text-sm transition active:scale-95"
                    data-testid="share-referral-btn"
                  >
                    <Share2 size={16} />
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="bg-white rounded-2xl border p-4">
            <h4 className="font-bold text-gray-700 text-sm mb-3">Tus referidos</h4>
            <div className="flex items-center justify-between">
              <div className="text-center flex-1">
                <p className="text-2xl font-black text-gray-900">{data.referral_count || 0}</p>
                <p className="text-xs text-gray-500">Invitados</p>
              </div>
              <div className="w-px h-10 bg-gray-100" />
              <div className="text-center flex-1">
                <p className="text-2xl font-black text-green-700">{((data.referral_count || 0) * (referralPromo ? referralPromo.bonus_points : 300)).toLocaleString('es-CL', { timeZone: 'America/Santiago' })}</p>
                <p className="text-xs text-gray-500">Puntos ganados</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-xl p-3">
            <p className="text-xs text-blue-700">
              📌 <strong>Importante:</strong> tu amigo debe completar su <strong>KYC</strong>, enviar mínimo <strong>$100.000 CLP</strong> por <strong>App o WhatsApp</strong> (no efectivo en oficina).
            </p>
          </div>
        </div>
      )}

      {/* HISTORY TAB */}
      {tab === 'history' && (
        <div data-testid="history-list">
          {(data.recent_transactions || []).length === 0 ? (
            <div className="bg-white rounded-2xl border p-8 text-center">
              <Clock size={32} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500 text-sm">Aun no tienes movimientos de puntos</p>
              <p className="text-gray-400 text-xs mt-1">Haz tu primer envio para ganar puntos</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border divide-y">
              {data.recent_transactions.map((tx, i) => (
                <div key={i} className="px-4 py-3 flex items-center justify-between" data-testid={`tx-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      tx.points > 0 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {tx.points > 0 ? '+' : '-'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{tx.description}</p>
                      <p className="text-xs text-gray-400">{new Date(tx.created_at).toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' })}</p>
                    </div>
                  </div>
                  <span className={`text-sm font-bold ${tx.points > 0 ? 'text-green-600' : 'text-orange-600'}`}>
                    {tx.points > 0 ? '+' : ''}{tx.points}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* HOW TO EARN TAB */}
      {tab === 'how' && (
        <div className="space-y-3" data-testid="how-to-earn">
          <div className="bg-white rounded-2xl border p-4">
            <h3 className="font-bold text-gray-800 text-sm mb-3">Gana puntos con cada envio desde Chile</h3>
            <div className="bg-gray-50 rounded-xl p-3 space-y-2">
              <p className="text-xs text-gray-600"><strong>Formula:</strong> Monto CLP / 1.000 x Multiplicador</p>
              <div className="space-y-1.5 mt-2">
                {[
                  { level: 'BRONZE', multi: '1.0x', example: '$50.000 = 50 pts' },
                  { level: 'SILVER', multi: '1.1x', example: '$50.000 = 55 pts' },
                  { level: 'GOLD', multi: '1.25x', example: '$50.000 = 62 pts' },
                ].map(l => (
                  <div key={l.level} className="flex items-center justify-between text-xs">
                    <span className="font-bold" style={{ color: LEVEL_STYLES[l.level].accent }}>{l.level} ({l.multi})</span>
                    <span className="text-gray-500">{l.example}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border p-4">
            <h3 className="font-bold text-gray-800 text-sm mb-3">Sube de nivel</h3>
            <div className="space-y-2">
              {[
                { level: 'SILVER', pts: '1.500 pts', desc: 'en ultimos 6 meses' },
                { level: 'GOLD', pts: '4.000 pts', desc: 'en ultimos 6 meses' },
              ].map(l => (
                <div key={l.level} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: LEVEL_STYLES[l.level].accent }}>
                    <Star size={16} fill="white" className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-800">{l.level}</p>
                    <p className="text-xs text-gray-500">{l.pts} {l.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl border p-4">
            <h3 className="font-bold text-gray-800 text-sm mb-2">Expiracion</h3>
            <p className="text-xs text-gray-500">Los puntos expiran 6 meses despues de ser ganados. Uselos antes.</p>
          </div>
          <div className="bg-white rounded-2xl border p-4">
            <h3 className="font-bold text-gray-800 text-sm mb-3">Reglas de Misiones</h3>
            <div className="space-y-2.5">
              <div className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-green-700 text-[10px] font-bold">1</span>
                </div>
                <p className="text-xs text-gray-600"><strong>Primer envio:</strong> Tu primer envio debe ser de <strong>$50.000 CLP o mas</strong> para ganar 100 pts</p>
              </div>
              <div className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-green-700 text-[10px] font-bold">2</span>
                </div>
                <p className="text-xs text-gray-600"><strong>Enviador frecuente:</strong> Envia <strong>$150.000 CLP en total</strong> en el mes para ganar 200 pts</p>
              </div>
              <div className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-green-700 text-[10px] font-bold">3</span>
                </div>
                <p className="text-xs text-gray-600"><strong>Gran envio:</strong> Un solo envio de <strong>$200.000 CLP o mas</strong> te da 150 pts</p>
              </div>
              <div className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-green-700 text-[10px] font-bold">4</span>
                </div>
                <p className="text-xs text-gray-600"><strong>Super enviador:</strong> Envia <strong>$500.000 CLP en total</strong> en el mes para ganar 500 pts</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border p-4">
            <h3 className="font-bold text-gray-800 text-sm mb-3">Reglas de Canje</h3>
            <div className="space-y-2">
              <p className="text-xs text-gray-600">Al canjear puntos recibes un <strong>bonus en CLP</strong> que se suma a tu proximo envio.</p>
              <div className="bg-blue-50 rounded-xl p-3">
                <p className="text-xs text-blue-700">El bonus solo se aplica en envios de <strong>{minBonusLabel}</strong> o mas.</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border p-4">
            <h3 className="font-bold text-gray-800 text-sm mb-3">Reglas de Referidos</h3>
            <div className="space-y-2">
              <p className="text-xs text-gray-600">Invita amigos con tu código y ambos ganan <strong>{referralPromo ? referralPromo.bonus_points : 300} puntos</strong> cuando tu amigo haga su primer envío de <strong>$100.000 CLP o más</strong>.</p>
              <ul className="text-xs text-gray-600 list-disc pl-4 space-y-0.5 mt-2">
                <li>Tu amigo debe completar su <strong>KYC</strong>.</li>
                <li>El envío debe ser por <strong>App o WhatsApp</strong> (no en oficina).</li>
                <li>Solo cuenta el <strong>primer</strong> envío del referido.</li>
              </ul>
              {referralPromo && (
                <div className="bg-pink-50 border border-pink-200 rounded-xl p-3 mt-2">
                  <p className="text-xs text-pink-700">🔥 <strong>Promo activa:</strong> {referralPromo.bonus_points} pts en vez de los 300 normales — canjeable por $3.000 CLP de bonus en envío.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GBPuntosClub;
